import { Check } from "lucide-react";
import { Icon } from "../../atoms/Icon/Icon";
import { cn } from "../../lib/cn";

export interface StepperStep {
  id: string;
  label: string;
  status: "complete" | "current" | "upcoming";
}

interface StepperProps {
  steps: StepperStep[];
  className?: string;
  // Fires for any step whose status is "complete" or "current" — lets a
  // caller offer back-nav to an already-completed step (or a no-op re-click
  // of the current one). "upcoming" steps never get a handler wired, since
  // there's nothing to jump back to.
  onStepClick?: (step: StepperStep) => void;
}

const MARKER_BASE = "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium";

// Numbered/checkmark circles joined by connector lines, with each step's
// label centered underneath its own circle — the familiar "campaign setup"
// stepper shape, not a segmented progress bar. From `sm:` up every step gets
// equal width via flex-1 (labels stay aligned under their circle regardless
// of label length); below `sm:` there isn't enough room to fit many steps at
// readable size, so each step gets a fixed column width instead and the
// track scrolls horizontally — squeezing every step's circle+label into an
// equal fraction of a 375px viewport is what produced the cramped, 3-line-
// wrapped labels this replaces. Each li owns the connector half-line on
// either side of its own circle so adjacent steps' half-lines join into one
// continuous line between them.
export function Stepper({ steps, className = "", onStepClick }: StepperProps) {
  return (
    <div className={cn("w-full overflow-x-auto", className)}>
      <ol className="flex list-none sm:min-w-full" aria-label="Progress">
        {steps.map((step, index) => {
          const isFirst = index === 0;
          const isLast = index === steps.length - 1;
          const clickable = onStepClick && step.status !== "upcoming";

          const marker = (
            <span
              className={cn(MARKER_BASE, step.status === "upcoming" ? "bg-neutral-400 text-white" : "bg-accent-600 text-white")}
            >
              {step.status === "complete" ? <Icon icon={Check} size="sm" /> : index + 1}
            </span>
          );

          return (
            <li key={step.id} className="relative flex w-20 shrink-0 flex-col items-center px-1 sm:w-auto sm:flex-1">
              {!isFirst && (
                <span className="absolute left-0 right-1/2 top-4 h-px -translate-y-1/2 bg-border" aria-hidden="true" />
              )}
              {!isLast && (
                <span className="absolute left-1/2 right-0 top-4 h-px -translate-y-1/2 bg-border" aria-hidden="true" />
              )}

              {clickable ? (
                <button
                  type="button"
                  onClick={() => onStepClick(step)}
                  aria-current={step.status === "current" ? "step" : undefined}
                  aria-label={step.label}
                  className="relative z-10 rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-600"
                >
                  {marker}
                </button>
              ) : (
                <div aria-current={step.status === "current" ? "step" : undefined} className="relative z-10">
                  {marker}
                </div>
              )}

              <span
                className={cn(
                  "mt-3 max-w-[5rem] text-center text-sm sm:max-w-[9rem]",
                  step.status === "upcoming" ? "text-text-subtle" : "font-medium text-text",
                )}
              >
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
